const sliderInstances = new Map();

class ProductGallerySliderController {
  constructor(galleryContainer) {
    this.galleryContainer = galleryContainer;
    this.mainGallerySelector = "[main-gallery-swiper]";
    this.thumbGallerySelector = ".thumb-gallery swiper-container";
    this.resizeTimer = null;
    this.thumbHeightStyleId = null;
    this.navigationRefreshTimers = [];
    this.mainSwiperUpdateTimer = null;
    this.hasPendingWindowLoadRefresh = false;
    this.handleResize = this.handleResize.bind(this);
    this.handleWindowLoad = this.handleWindowLoad.bind(this);
    this.handleMainMediaLoad = this.handleMainMediaLoad.bind(this);

    this.startInitialisation();
  }

  shouldSyncThumbHeight() {
    if (typeof window === "undefined") return false;
    return window.innerWidth > 992;
  }

  startInitialisation() {
    if (window.customElements && typeof window.customElements.whenDefined === "function") {
      if (window.customElements.get("swiper-container")) {
        this.init();
      } else {
        window.customElements
          .whenDefined("swiper-container")
          .then(() => {
            if (this.galleryContainer?.isConnected) {
              this.init();
            }
          })
          .catch((error) => {
            console.warn(
              "ProductGallerySliderController: Failed to wait for swiper-container",
              error,
            );
            this.markGalleryReady();
          });
      }
    } else {
      this.init();
    }
  }

  init() {
    if (!this.galleryContainer) {
      return;
    }

    this.mainGallery = this.galleryContainer.querySelector(this.mainGallerySelector);
    this.thumbGallery = this.galleryContainer.querySelector(this.thumbGallerySelector);
    this.thumbPositioning = this.galleryContainer.getAttribute("data-thumb-positioning") || "left";

    if (!this.mainGallery) {
      return;
    }

    this.mainGallery.addEventListener("load", this.handleMainMediaLoad, true);
    this.setupResizeListener();
    this.initGallery();
  }

  refreshMainSwiper(swiperInstance = this.mainGallery?.swiper) {
    if (!swiperInstance || swiperInstance.destroyed) {
      return;
    }

    if (typeof swiperInstance.update === "function") {
      try {
        swiperInstance.update();
      } catch (error) {
        console.warn("ProductGallerySliderController: Failed to update main swiper", error);
      }
    }

    this.refreshMainNavigation(swiperInstance);
  }

  queueMainSwiperRefresh(delay = 0) {
    if (typeof window === "undefined") {
      this.refreshMainSwiper();
      return;
    }

    if (this.mainSwiperUpdateTimer) {
      window.clearTimeout(this.mainSwiperUpdateTimer);
    }

    this.mainSwiperUpdateTimer = window.setTimeout(() => {
      this.mainSwiperUpdateTimer = null;
      this.refreshMainSwiper();
    }, delay);
  }

  handleMainMediaLoad(event) {
    const loadedElement = event?.target;
    if (
      !(loadedElement instanceof HTMLElement) ||
      !loadedElement.closest("[main-gallery-swiper]")
    ) {
      return;
    }

    this.queueMainSwiperRefresh(40);
  }

  getMainNavigationButtons() {
    if (!this.galleryContainer) {
      return { prevButton: null, nextButton: null };
    }

    const mainGalleryRoot = this.galleryContainer.querySelector(".main-gallery");
    if (!mainGalleryRoot) {
      return { prevButton: null, nextButton: null };
    }

    return {
      prevButton: mainGalleryRoot.querySelector(".swiper-button-prev"),
      nextButton: mainGalleryRoot.querySelector(".swiper-button-next"),
    };
  }

  hasMatchingNavigationElement(candidate, target) {
    if (!candidate || !target) return false;
    if (Array.isArray(candidate)) return candidate.includes(target);
    return candidate === target;
  }

  getMainActiveIndex(swiperInstance = this.mainGallery?.swiper) {
    if (!swiperInstance || swiperInstance.destroyed) {
      return 0;
    }

    if (typeof swiperInstance.realIndex === "number" && !Number.isNaN(swiperInstance.realIndex)) {
      return swiperInstance.realIndex;
    }

    if (
      typeof swiperInstance.activeIndex === "number" &&
      !Number.isNaN(swiperInstance.activeIndex)
    ) {
      return swiperInstance.activeIndex;
    }

    return 0;
  }

  syncMainNavigationState(
    swiperInstance = this.mainGallery?.swiper,
    prevButton = null,
    nextButton = null,
  ) {
    if (!swiperInstance || swiperInstance.destroyed) {
      return;
    }

    const resolvedPrevButton = prevButton || this.getMainNavigationButtons().prevButton;
    const resolvedNextButton = nextButton || this.getMainNavigationButtons().nextButton;
    if (!resolvedPrevButton || !resolvedNextButton) {
      return;
    }

    const totalSlides = Array.isArray(swiperInstance.slides)
      ? swiperInstance.slides.length
      : Array.from(swiperInstance.slides || []).length;
    const activeIndex = this.getMainActiveIndex(swiperInstance);
    const atStart = totalSlides <= 1 || activeIndex <= 0;
    const atEnd = totalSlides <= 1 || activeIndex >= totalSlides - 1;

    resolvedPrevButton.classList.toggle("swiper-button-disabled", atStart);
    resolvedPrevButton.setAttribute("aria-disabled", String(atStart));
    resolvedPrevButton.toggleAttribute("disabled", atStart);

    resolvedNextButton.classList.toggle("swiper-button-disabled", atEnd);
    resolvedNextButton.setAttribute("aria-disabled", String(atEnd));
    resolvedNextButton.toggleAttribute("disabled", atEnd);
  }

  syncThumbWithMain(swiperInstance = this.mainGallery?.swiper) {
    const thumbSwiper = this.thumbGallery?.swiper;
    if (!swiperInstance || swiperInstance.destroyed || !thumbSwiper || thumbSwiper.destroyed) {
      return;
    }

    const targetIndex = this.getMainActiveIndex(swiperInstance);
    if (typeof thumbSwiper.slideTo === "function" && thumbSwiper.activeIndex !== targetIndex) {
      thumbSwiper.slideTo(targetIndex);
    }

    if (typeof thumbSwiper.updateSlidesClasses === "function") {
      thumbSwiper.updateSlidesClasses();
    }
  }

  refreshMainNavigation(swiperInstance = this.mainGallery?.swiper) {
    if (!swiperInstance || swiperInstance.destroyed) {
      return;
    }

    const { prevButton, nextButton } = this.getMainNavigationButtons();
    if (!prevButton || !nextButton) {
      return;
    }

    const hasNavigationModule = Boolean(swiperInstance.navigation);
    const navParams =
      swiperInstance.params && typeof swiperInstance.params.navigation === "object"
        ? swiperInstance.params.navigation
        : {};

    const prevMatchesCurrent = this.hasMatchingNavigationElement(
      swiperInstance.navigation?.prevEl,
      prevButton,
    );
    const nextMatchesCurrent = this.hasMatchingNavigationElement(
      swiperInstance.navigation?.nextEl,
      nextButton,
    );

    navParams.prevEl = prevButton;
    navParams.nextEl = nextButton;
    swiperInstance.params.navigation = navParams;

    if (hasNavigationModule) {
      try {
        if (!prevMatchesCurrent || !nextMatchesCurrent) {
          swiperInstance.navigation.destroy();
          swiperInstance.navigation.init();
        }
        swiperInstance.navigation.update();
      } catch (error) {
        console.warn("ProductGallerySliderController: Failed to refresh main navigation", error);
      }
    }

    this.syncMainNavigationState(swiperInstance, prevButton, nextButton);
  }

  clearNavigationRefreshTimers() {
    if (typeof window === "undefined") {
      this.navigationRefreshTimers = [];
      return;
    }

    this.navigationRefreshTimers.forEach((timerId) => {
      window.clearTimeout(timerId);
    });
    this.navigationRefreshTimers = [];
  }

  scheduleNavigationRefreshes(swiperInstance) {
    if (typeof window === "undefined") {
      this.refreshMainSwiper(swiperInstance);
      return;
    }

    this.clearNavigationRefreshTimers();
    [0, 200, 600, 1200, 2200].forEach((delay) => {
      const timerId = window.setTimeout(() => {
        this.refreshMainSwiper(swiperInstance);
      }, delay);
      this.navigationRefreshTimers.push(timerId);
    });

    if (document.readyState === "complete") {
      this.refreshMainSwiper(swiperInstance);
      return;
    }

    if (!this.hasPendingWindowLoadRefresh) {
      window.addEventListener("load", this.handleWindowLoad, { once: true });
      this.hasPendingWindowLoadRefresh = true;
    }
  }

  handleWindowLoad() {
    this.hasPendingWindowLoadRefresh = false;
    this.refreshMainSwiper();
  }

  syncThumbHeight() {
    if (this.thumbPositioning === "under") {
      return;
    }

    if (!this.mainGallery || !this.thumbGallery) {
      return;
    }

    const mainHeight = this.mainGallery.getBoundingClientRect().height;
    if (mainHeight <= 0) {
      return;
    }

    const thumbGalleryWrapper = this.thumbGallery.closest(".thumb-gallery");
    if (!thumbGalleryWrapper) {
      return;
    }

    thumbGalleryWrapper.style.height = `${mainHeight}px`;
    thumbGalleryWrapper.style.maxHeight = `${mainHeight}px`;
    thumbGalleryWrapper.style.minHeight = `${mainHeight}px`;
    thumbGalleryWrapper.style.overflow = "hidden";

    this.thumbGallery.style.height = "100%";
    this.thumbGallery.style.maxHeight = "100%";
    this.thumbGallery.style.overflow = "hidden";

    this.injectThumbSwiperStyles(mainHeight);

    if (this.thumbGallery.swiper && typeof this.thumbGallery.swiper.update === "function") {
      this.thumbGallery.swiper.update();
    }
  }

  cleanupThumbHeight() {
    if (!this.thumbGallery) return;

    const thumbGalleryWrapper = this.thumbGallery.closest(".thumb-gallery");

    if (this.thumbHeightStyleId) {
      const existingStyle = document.getElementById(this.thumbHeightStyleId);
      if (existingStyle) existingStyle.remove();
      this.thumbHeightStyleId = null;
    }

    if (thumbGalleryWrapper) {
      thumbGalleryWrapper.style.height = "";
      thumbGalleryWrapper.style.maxHeight = "";
      thumbGalleryWrapper.style.minHeight = "";
      thumbGalleryWrapper.style.overflow = "";
    }

    this.thumbGallery.style.height = "";
    this.thumbGallery.style.maxHeight = "";
    this.thumbGallery.style.overflow = "";
  }

  injectThumbSwiperStyles(mainHeight) {
    if (!this.thumbGallery?.id) {
      return;
    }

    const styleId = `thumb-swiper-height-fix-${this.thumbGallery.id}`;
    this.thumbHeightStyleId = styleId;

    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      #${this.thumbGallery.id} {
        height: ${mainHeight}px !important;
        max-height: ${mainHeight}px !important;
      }
      #${this.thumbGallery.id}::part(wrapper) {
        height: 100% !important;
        max-height: 100% !important;
        flex-direction: column !important;
      }
    `;
    document.head.appendChild(style);
  }

  setupResizeListener() {
    if (typeof window === "undefined") {
      return;
    }

    window.addEventListener("resize", this.handleResize, { passive: true });
  }

  handleResize() {
    if (!this.galleryContainer?.isConnected) {
      this.destroy();
      return;
    }

    if (this.resizeTimer) {
      window.clearTimeout(this.resizeTimer);
    }

    this.resizeTimer = window.setTimeout(() => {
      this.resizeTimer = null;
      if (this.shouldSyncThumbHeight()) {
        this.syncThumbHeight();
      } else {
        this.cleanupThumbHeight();
      }
      this.refreshMainSwiper();
    }, 250);
  }

  markGalleryReady() {
    if (this.galleryContainer) {
      this.galleryContainer.classList.remove("opacity-0");
      this.galleryContainer.classList.add("opacity-1");

      // Show yellow-splash element with transition
      const yellowSplash = this.galleryContainer.querySelector(".yellow-splash");
      if (yellowSplash) {
        setTimeout(() => {
          yellowSplash.classList.remove("opacity-0");
          yellowSplash.classList.add("opacity-1");
        }, 200); // Small delay for smoother transition
      }
    }
  }

  initialiseSwiperElement(element) {
    if (!element) {
      return Promise.resolve(null);
    }

    if (element.swiper) {
      return Promise.resolve(element.swiper);
    }

    if (typeof element.initialize !== "function") {
      return Promise.resolve(null);
    }

    try {
      const initResult = element.initialize();
      if (initResult && typeof initResult.then === "function") {
        return initResult.then(() => element.swiper || null);
      }
      return Promise.resolve(element.swiper || null);
    } catch (error) {
      console.error("ProductGallerySliderController: Error during element initialization:", error);
      return Promise.reject(error);
    }
  }

  initGallery() {
    this.initialiseSwiperElement(this.thumbGallery)
      .then(() => this.initialiseSwiperElement(this.mainGallery))
      .then((mainSwiperInstance) => {
        if (mainSwiperInstance) {
          window.gallery_swiper = mainSwiperInstance;
          this.refreshMainSwiper(mainSwiperInstance);
          this.syncThumbWithMain(mainSwiperInstance);
          if (typeof mainSwiperInstance.on === "function") {
            mainSwiperInstance.on("slideChange", () => {
              this.refreshMainNavigation(mainSwiperInstance);
              this.syncThumbWithMain(mainSwiperInstance);
            });
          }
          this.scheduleNavigationRefreshes(mainSwiperInstance);
        }
        if (this.thumbGallery?.swiper) {
          window.gallery_thumb_swiper = this.thumbGallery.swiper;
        }

        this.markGalleryReady();

        if (this.shouldSyncThumbHeight()) {
          window.setTimeout(() => {
            this.syncThumbHeight();
          }, 100);
        } else {
          this.cleanupThumbHeight();
        }
      })
      .catch((error) => {
        console.error("ProductGallerySliderController: Failed to initialise Swiper:", error);
        this.markGalleryReady();
      });
  }

  destroy() {
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.handleResize);
      if (this.hasPendingWindowLoadRefresh) {
        window.removeEventListener("load", this.handleWindowLoad);
        this.hasPendingWindowLoadRefresh = false;
      }
    }

    if (this.resizeTimer) {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = null;
    }

    if (this.mainSwiperUpdateTimer) {
      window.clearTimeout(this.mainSwiperUpdateTimer);
      this.mainSwiperUpdateTimer = null;
    }

    this.clearNavigationRefreshTimers();

    if (this.mainGallery) {
      this.mainGallery.removeEventListener("load", this.handleMainMediaLoad, true);
    }

    if (this.mainGallery?.swiper && !this.mainGallery.swiper.destroyed) {
      this.mainGallery.swiper.destroy(true, true);
    }
    if (this.thumbGallery?.swiper && !this.thumbGallery.swiper.destroyed) {
      this.thumbGallery.swiper.destroy(true, true);
    }

    this.cleanupThumbHeight();
  }
}

function findGalleryContainers(root = document) {
  if (!root) return [];
  if (root instanceof Element && root.matches("[gallery-container]")) {
    return [root, ...root.querySelectorAll("[gallery-container]")];
  }
  if (typeof root.querySelectorAll !== "function") return [];
  return Array.from(root.querySelectorAll("[gallery-container]"));
}

export function initProductGallerySliders(root = document) {
  const containers = findGalleryContainers(root);
  containers.forEach((container) => {
    if (!container || sliderInstances.has(container)) {
      return;
    }
    sliderInstances.set(container, new ProductGallerySliderController(container));
  });
}

export function destroyProductGallerySliders(root = document) {
  const containers = findGalleryContainers(root);
  containers.forEach((container) => {
    const instance = sliderInstances.get(container);
    if (!instance) return;
    instance.destroy();
    sliderInstances.delete(container);
  });
}

function onDomReady(callback) {
  if (typeof document === "undefined") return;
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", callback, { once: true });
  } else {
    callback();
  }
}

onDomReady(() => initProductGallerySliders(document));

if (typeof document !== "undefined") {
  document.addEventListener("shopify:section:load", (event) => {
    initProductGallerySliders(event?.target || document);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    destroyProductGallerySliders(event?.target || document);
  });
}

if (typeof window !== "undefined") {
  window.QF = window.QF || {};
  window.QF.productGallerySlider = {
    init: initProductGallerySliders,
    destroy: destroyProductGallerySliders,
    instances: sliderInstances,
  };
}
